import React from "react"
import Swal from "sweetalert2"
import { toast } from "react-toastify"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { PageTitle, PageLink } from "@common-metronic/layout/core"
import CenteredLoader from "./_CenteredLoader"
import { IPersonnelDemandeEmbaucheModel } from "../core/_models"
import {
     personnelDEGetDetailsRequest,
     personnelDEDocumentsEmbauchePreviewRequest,
     personnelDEEnvoyerDocumentsEmbaucheRequest,
     personnelDETelechargerDocumentsEmbaucheRequest,
     IPersonnelDEDocumentsEmbauchePreviewModel,
     IPersonnelDocumentsEmbaucheFileModel,
} from "../core/_requests"
import MyGoogleDriveFileViewerInModal from "../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { triggerBlobDownload, formatFileSize, MAX_EMAIL_ATTACHMENT_SIZE } from "../core/_download"
import {
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS,
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE,
} from "../../gestion/routing/routing-endpoints"
import { ROUTING_BREADCRUMBS_GESTION_STRUCTURE_STAFF } from "../../gestion/routing/routing-breadcrumbs"
import { formatPrenomNom } from "../core/_nameFormat"
import { driveImageUrl } from "../core/_avatar"

/**
 * Page « Envoyer les documents d'embauche » : le créateur envoie au collaborateur (email personnel) les
 * documents d'embauche du dossier Drive de son entité de rattachement, + éventuellement son contrat de
 * travail (coché par défaut). Salarié uniquement, demande en configuration IT.
 */
const EnvoyerDocumentsEmbauche = () => {
     const navigate = useNavigate()
     const { id } = useParams<{ id: string }>()
     const demandeId = Number(id)

     const [demande, setDemande] = React.useState<IPersonnelDemandeEmbaucheModel | null>(null)
     const [preview, setPreview] = React.useState<IPersonnelDEDocumentsEmbauchePreviewModel | null>(null)
     const [loading, setLoading] = React.useState<boolean>(true)
     const [error, setError] = React.useState<string | null>(null)
     const [contratInclus, setContratInclus] = React.useState<boolean>(true) // coché par défaut
     const [submitting, setSubmitting] = React.useState<boolean>(false)
     const [downloading, setDownloading] = React.useState<boolean>(false)
     const [activeDoc, setActiveDoc] = React.useState<IPersonnelDocumentsEmbaucheFileModel | null>(null)

     const reloadPreview = React.useCallback(() => {
          return personnelDEDocumentsEmbauchePreviewRequest(demandeId).then(pv => setPreview(pv.data))
     }, [demandeId])

     React.useEffect(() => {
          Promise.all([personnelDEGetDetailsRequest(demandeId), personnelDEDocumentsEmbauchePreviewRequest(demandeId)])
               .then(([d, pv]) => {
                    setDemande(d.data)
                    setPreview(pv.data)
               })
               .catch(e => setError(e?.response?.data?.detail ?? "Impossible de charger les documents d'embauche."))
               .finally(() => setLoading(false))
     }, [demandeId])

     if (loading) return <CenteredLoader />
     if (error) return <MyAlert type={"danger"} isDismissible={false}><>{error}</></MyAlert>
     if (!demande || !preview) return <MyAlert type={"danger"} isDismissible={false}><>Demande introuvable.</></MyAlert>

     const p = demande.personnel
     const px = (p ?? {}) as any
     const nomComplet = formatPrenomNom(p?.prenom, p?.nom)
     const contratSelectionne = contratInclus && preview.contratDisponible
     const nbDocs = preview.documentsEntite.length + (contratSelectionne ? 1 : 0)
     const noMail = !preview.mailPersonnel
     const suivis = preview.suivis ?? []

     // Taille totale de l'envoi = documents de l'entité + (contrat si sélectionné). Bloque l'e-mail au-delà de la limite.
     const maxEmailSize = preview.maxEmailSize ?? MAX_EMAIL_ATTACHMENT_SIZE
     const totalSize =
          preview.documentsEntite.reduce((n, d) => n + (d.size ?? 0), 0) + (contratSelectionne ? preview.contratSize ?? 0 : 0)
     const tooLarge = totalSize > maxEmailSize
     const dejaEnvoye = suivis.length > 0
     // suivis triés du plus récent au plus ancien → le premier est le dernier envoi.
     const dernierEnvoi = suivis[0]?.date ? moment(suivis[0].date).format("DD/MM/YYYY à HH:mm") : null

     // Fil d'ariane : Gestion . Structure . Staff . Demandes d'embauche . <Prénom NOM> . Documents d'embauche
     const breadcrumbs: Array<PageLink> = [
          ...ROUTING_BREADCRUMBS_GESTION_STRUCTURE_STAFF,
          { title: "Demandes d'embauche", path: ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDES_EMBAUCHE },
          { title: nomComplet, path: ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS(demandeId) },
     ]

     function handleEnvoyer() {
          if (!preview?.mailPersonnel) return
          // Rappel si les documents ont déjà été envoyés (le créateur pourrait l'avoir oublié).
          const rappelDejaEnvoye = dejaEnvoye
               ? `<div class="alert alert-warning text-start mb-4">⚠️ Ces documents ont <b>déjà été envoyés</b>${dernierEnvoi ? ` le <b>${dernierEnvoi}</b>` : ""}. Vous êtes sur le point de les renvoyer.</div>`
               : ""
          Swal.fire({
               icon: dejaEnvoye ? "warning" : "question",
               title: dejaEnvoye ? "Renvoyer les documents d'embauche ?" : "Envoyer les documents d'embauche ?",
               html: `${rappelDejaEnvoye}Les documents seront envoyés à <b>${preview.mailPersonnel}</b>.`,
               showCancelButton: true,
               confirmButtonText: dejaEnvoye ? "Renvoyer" : "Envoyer",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#50cd89",
          }).then(res => {
               if (!res.isConfirmed) return
               setSubmitting(true)
               personnelDEEnvoyerDocumentsEmbaucheRequest(demandeId, contratInclus)
                    .then(async () => {
                         toast.success("Les documents d'embauche ont été envoyés.")
                         await reloadPreview()
                    })
                    .catch(e => toast.error(e?.response?.data?.detail ?? "L'envoi a échoué.", { autoClose: false }))
                    .finally(() => setSubmitting(false))
          })
     }

     function handleTelecharger() {
          setDownloading(true)
          personnelDETelechargerDocumentsEmbaucheRequest(demandeId, contratInclus)
               .then(r => triggerBlobDownload(r.data, `documents-embauche-${nomComplet || demandeId}.zip`))
               .catch(async e => {
                    // La réponse d'erreur est un blob (responseType blob) : on tente d'en extraire le détail.
                    const detail = e?.response?.data instanceof Blob ? await e.response.data.text().then((t: string) => JSON.parse(t)?.detail).catch(() => null) : e?.response?.data?.detail
                    toast.error(detail ?? "Le téléchargement a échoué.", { autoClose: false })
               })
               .finally(() => setDownloading(false))
     }

     return (
          <>
               <PageTitle breadcrumbs={breadcrumbs}>Documents d'embauche</PageTitle>

               {/* En-tête : collaborateur concerné + état de l'envoi */}
               <div className="card mb-4">
                    <div className="card-body d-flex flex-column flex-sm-row justify-content-between align-items-start gap-3">
                         <div className="d-flex align-items-center gap-3">
                              <div className="symbol symbol-60px symbol-circle">
                                   {px?.avatarGoogleDriveId ? (
                                        <img src={driveImageUrl(px.avatarGoogleDriveId)} referrerPolicy="no-referrer" alt={nomComplet} style={{ objectFit: "cover" }} />
                                   ) : (
                                        <span className="symbol-label bg-light-primary text-primary">
                                             <i className="fas fa-user fs-2" />
                                        </span>
                                   )}
                              </div>
                              <div>
                                   <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <h3 className="mb-0">{nomComplet}</h3>
                                        {dejaEnvoye && <span className="badge badge-light-success"><i className="fas fa-paper-plane me-1" /> Documents envoyés</span>}
                                   </div>
                                   <div className="text-muted">Envoi des documents d'embauche au collaborateur</div>
                              </div>
                         </div>
                    </div>
               </div>

               {/* Rappel : documents déjà envoyés (le créateur pourrait l'avoir oublié) */}
               {dejaEnvoye && (
                    <MyAlert type={"warning"} classNames={"mb-4"} isDismissible={false}>
                         <>
                              Ces documents d'embauche ont <strong>déjà été envoyés</strong>
                              {dernierEnvoi ? (
                                   <>
                                        {" "}le <strong>{dernierEnvoi}</strong>
                                   </>
                              ) : null}
                              .
                         </>
                    </MyAlert>
               )}

               <div className="row">
                    {/* Colonne principale : destinataire + documents */}
                    <div className="col-lg-8">
                         <div className="card mb-4">
                              <div className="card-header">
                                   <h4 className="card-title">Destinataire</h4>
                              </div>
                              <div className="card-body">
                                   <MyAlert type={"primary"} classNames={"mb-6"} isDismissible={false}>
                                        <>
                                             Les documents d'embauche du dossier Drive de l'entité de rattachement seront envoyés à l'adresse email personnelle du
                                             collaborateur. Vous recevrez une copie de cet envoi.
                                        </>
                                   </MyAlert>

                                   {noMail && (
                                        <MyAlert type={"warning"} classNames={"mb-6"} isDismissible={false}>
                                             <>Le collaborateur n'a pas d'adresse email personnelle renseignée : impossible d'envoyer les documents.</>
                                        </MyAlert>
                                   )}

                                   <div className="row g-4">
                                        <div className="col-md-6">
                                             <div className="d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded p-4 h-100">
                                                  <span className="symbol symbol-40px">
                                                       <span className="symbol-label bg-light-primary text-primary"><i className="fas fa-building" /></span>
                                                  </span>
                                                  <div className="min-w-0">
                                                       <div className="text-muted fs-8 text-uppercase fw-bold">Entité &amp; statut</div>
                                                       <div className="fw-bold fs-6 text-truncate">
                                                            {preview.entite ?? "—"}
                                                            {preview.statut && <span className="badge badge-light-info ms-2">{preview.statut}</span>}
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                        <div className="col-md-6">
                                             <div className="d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded p-4 h-100">
                                                  <span className="symbol symbol-40px">
                                                       <span className={`symbol-label ${noMail ? "bg-light-danger text-danger" : "bg-light-success text-success"}`}><i className="fas fa-envelope" /></span>
                                                  </span>
                                                  <div className="min-w-0">
                                                       <div className="text-muted fs-8 text-uppercase fw-bold">Email personnel</div>
                                                       <div className={`fw-bold fs-6 text-truncate ${noMail ? "text-danger" : ""}`}>{preview.mailPersonnel ?? "Non renseigné"}</div>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         </div>

                         <div className="card mb-4">
                              <div className="card-header">
                                   <h4 className="card-title">Documents qui seront envoyés</h4>
                                   <div className="card-toolbar">
                                        <span className={`badge ${nbDocs > 0 ? "badge-light-primary" : "badge-light-danger"} fs-7`}>{nbDocs} document{nbDocs > 1 ? "s" : ""}</span>
                                   </div>
                              </div>
                              <div className="card-body">
                                   {/* Contrat de travail : coché par défaut, décochable. */}
                                   <label
                                        htmlFor="contratInclus"
                                        className={`d-flex align-items-center gap-3 rounded p-4 mb-4 ${preview.contratDisponible ? "bg-light-primary cursor-pointer" : "bg-light-danger"}`}
                                   >
                                        <input
                                             className="form-check-input"
                                             type="checkbox"
                                             id="contratInclus"
                                             checked={contratSelectionne}
                                             disabled={!preview.contratDisponible}
                                             onChange={e => setContratInclus(e.target.checked)}
                                        />
                                        <i className="fas fa-file-signature fs-2 text-primary" />
                                        <div>
                                             <div className="fw-bold text-gray-900">Contrat de travail</div>
                                             {preview.contratDisponible ? (
                                                  <div className="text-muted fs-7">Inclus par défaut — décochez pour ne pas l'envoyer.</div>
                                             ) : (
                                                  <div className="text-danger fs-7">Aucun contrat de travail disponible pour le moment.</div>
                                             )}
                                        </div>
                                   </label>

                                   <div className="d-flex align-items-center mb-3">
                                        <i className="fas fa-building text-muted me-2" />
                                        <span className="fw-bold text-gray-800">{preview.entite ?? "Entité de rattachement"}</span>
                                        {preview.statut && <span className="badge badge-light-info ms-2">{preview.statut}</span>}
                                        <span className="badge badge-light ms-2">{preview.documentsEntite.length}</span>
                                   </div>
                                   <div className="text-muted fs-8 mb-3">Les documents dépendent de l'entité de rattachement et du statut du collaborateur.</div>
                                   {preview.documentsEntite.length ? (
                                        <div className="d-flex flex-column gap-2">
                                             {preview.documentsEntite.map((doc, i) => (
                                                  <div
                                                       key={i}
                                                       className="d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded px-4 py-3 cursor-pointer bg-hover-light-primary"
                                                       onClick={() => setActiveDoc(doc)}
                                                       title="Cliquez pour visualiser"
                                                  >
                                                       <i className="fas fa-file-pdf fs-3 text-danger" />
                                                       <span className="text-gray-800 fw-semibold text-truncate">{doc.name ?? "Document"}</span>
                                                       {doc.size != null && <span className="badge badge-light ms-auto">{formatFileSize(doc.size)}</span>}
                                                       <i className="fas fa-eye text-muted" />
                                                  </div>
                                             ))}
                                        </div>
                                   ) : (
                                        <MyAlert type={"warning"} isDismissible={false}>
                                             <>Aucun document d'embauche n'est configuré pour cette entité et ce statut.</>
                                        </MyAlert>
                                   )}

                                   {/* Taille totale + garde d'envoi e-mail */}
                                   {nbDocs > 0 && (
                                        <div className={`d-flex align-items-center justify-content-between mt-4 p-3 rounded ${tooLarge ? "bg-light-danger" : "bg-light"}`}>
                                             <span className="fw-semibold text-gray-800">Taille totale de l'envoi</span>
                                             <span className={`fw-bold ${tooLarge ? "text-danger" : "text-gray-800"}`}>
                                                  {formatFileSize(totalSize)} / {formatFileSize(maxEmailSize)}
                                             </span>
                                        </div>
                                   )}
                                   {tooLarge && (
                                        <MyAlert type={"danger"} classNames={"mt-3 mb-0"} isDismissible={false}>
                                             <>
                                                  Les documents dépassent la taille maximale autorisée pour un envoi par e-mail ({formatFileSize(maxEmailSize)}). Décochez le contrat ou
                                                  utilisez plutôt le téléchargement du ZIP.
                                             </>
                                        </MyAlert>
                                   )}
                              </div>
                         </div>
                    </div>

                    {/* Colonne latérale : actions + historique */}
                    <div className="col-lg-4">
                         <div className="card mb-4">
                              <div className="card-header">
                                   <h4 className="card-title">Actions</h4>
                              </div>
                              <div className="card-body d-flex flex-column gap-3">
                                   <button
                                        type="button"
                                        className="btn btn-success d-inline-flex align-items-center justify-content-center"
                                        onClick={handleEnvoyer}
                                        disabled={submitting || noMail || nbDocs === 0 || tooLarge}
                                        title={tooLarge ? "Taille trop importante pour un envoi par e-mail" : undefined}
                                   >
                                        {submitting ? (
                                             <MySimpleSpinner size={"sm"} />
                                        ) : (
                                             <>
                                                  <i className="fas fa-paper-plane me-2" /> {dejaEnvoye ? "Renvoyer" : "Envoyer"} {nbDocs > 0 ? `(${nbDocs})` : ""}
                                             </>
                                        )}
                                   </button>
                                   <button
                                        type="button"
                                        className="btn btn-light-primary d-inline-flex align-items-center justify-content-center"
                                        onClick={handleTelecharger}
                                        disabled={downloading || nbDocs === 0}
                                   >
                                        {downloading ? (
                                             <MySimpleSpinner size={"sm"} />
                                        ) : (
                                             <>
                                                  <i className="fas fa-download me-2" /> Télécharger le ZIP
                                             </>
                                        )}
                                   </button>
                                   <div className="separator separator-dashed" />
                                   <button
                                        type="button"
                                        className="btn btn-light d-inline-flex align-items-center justify-content-center"
                                        onClick={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS(demandeId))}
                                   >
                                        <i className="fas fa-arrow-left me-2" /> Retour à la demande
                                   </button>
                              </div>
                         </div>

                         {/* Journal d'activité : envois déjà enregistrés */}
                         {suivis.length > 0 && (
                              <div className="card mb-4">
                                   <div className="card-header">
                                        <h4 className="card-title">Historique</h4>
                                   </div>
                                   <div className="card-body">
                                        <div className="timeline timeline-border-dashed">
                                             {suivis.map((s, i) => (
                                                  <div className="timeline-item" key={i}>
                                                       <div className="timeline-icon">
                                                            <i className="fas fa-paper-plane text-success fs-4" />
                                                       </div>
                                                       <div className="timeline-content mb-6">
                                                            <div className="fw-bold text-gray-900">{s.typeLabel ?? s.type}</div>
                                                            <div className="text-muted fs-7">
                                                                 {s.date ? moment(s.date).format("DD/MM/YYYY à HH:mm") : ""}
                                                                 {s.auteur ? ` — ${s.auteur}` : ""}
                                                            </div>
                                                            {s.commentaire && <div className="fs-7 text-gray-700 mt-1">{s.commentaire}</div>}
                                                       </div>
                                                  </div>
                                             ))}
                                        </div>
                                   </div>
                              </div>
                         )}
                    </div>
               </div>

               {activeDoc && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.googleDriveID}
                         googleDriveMimetype={activeDoc.mimeType ?? ""}
                         modalTitle={<>{activeDoc.name ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}
          </>
     )
}

export default EnvoyerDocumentsEmbauche
